/**
 * FlyToCart — Fly-to-cart animation with tornado funnel effect.
 *
 * When the user taps "Add to Cart", the product card flies straight down
 * into the cart icon with a 3D tornado distortion, particle trail, and impact effects.
 *
 * Uses html2canvas to capture the card as an image — avoids DOM stacking/overflow
 * clipping when the widget is in an iframe or a container with overflow:hidden.
 *
 * Usage:
 *   const flyToCart = new FlyToCart({
 *     getCartTarget: () => document.querySelector('.ttp-cart-icon'),
 *     onCartBump: () => { ... },
 *   });
 *   flyToCart.triggerFly(cardElement, product, () => { /* add to cart API *\/ });
 */

import html2canvas from 'html2canvas';

const DURATION = 1000;

export class FlyToCart {
  constructor(options = {}) {
    this.getCartTarget = options.getCartTarget || (() => null);
    this.onCartBump = options.onCartBump || (() => {});
    this.onRipple = options.onRipple || (() => {});

    this.animatingId = null;
    this.trailParticles = [];
    this.canvas = null;
    this.rafId = null;
    this._canvasInitialized = false;
  }

  get isAnimating() {
    return this.animatingId !== null;
  }

  _ensureCanvas() {
    if (this._canvasInitialized) return;
    this._canvasInitialized = true;

    this.canvas = document.createElement('canvas');
    this.canvas.id = 'ttp-fly-to-cart-canvas';
    this.canvas.style.cssText = `
      position: fixed; top: 0; left: 0;
      width: 100%; height: 100%;
      pointer-events: none; z-index: 2147483646;
    `;
    document.body.appendChild(this.canvas);

    const ctx = this.canvas.getContext('2d');
    const resize = () => {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const drawTrail = () => {
      if (!this.canvas || !ctx) return;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.trailParticles = this.trailParticles.filter(p => p.life > 0);
      for (const p of this.trailParticles) {
        p.life -= 0.025;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.96;
        p.vy *= 0.96;
        p.r *= 0.97;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color}, ${p.life * 0.6})`;
        ctx.fill();
      }
      this.rafId = requestAnimationFrame(drawTrail);
    };
    this.rafId = requestAnimationFrame(drawTrail);
  }

  _emitTrailAt(x, y, progress) {
    for (let i = 0; i < 3; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.5 + Math.random() * 2;
      const spread = (1 - progress) * 3;
      this.trailParticles.push({
        x: x + (Math.random() - 0.5) * 20 * spread,
        y: y + (Math.random() - 0.5) * 10,
        vx: Math.cos(angle) * speed * spread,
        vy: Math.sin(angle) * speed * 0.5,
        r: 2 + Math.random() * 4 * (1 - progress),
        life: 0.6 + Math.random() * 0.4,
        color: Math.random() > 0.5 ? '139, 92, 246' : '236, 72, 153',
      });
    }
  }

  _emitImpactBurst(x, y) {
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      this.trailParticles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: 2 + Math.random() * 3,
        life: 0.8 + Math.random() * 0.2,
        color: i % 3 === 0 ? '250, 204, 21' : i % 3 === 1 ? '74, 222, 128' : '139, 92, 246',
      });
    }
  }

  triggerFly(cardElement, product, onComplete, options = {}) {
    if (this.animatingId !== null) return;

    const cartTarget = this.getCartTarget();
    if (!cardElement || !cartTarget) {
      if (onComplete) onComplete();
      return;
    }

    const cardRect = cardElement.getBoundingClientRect();

    const getTargetCenter = () => {
      const liveTarget = this.getCartTarget();
      if (!liveTarget) return { x: window.innerWidth - 60, y: window.innerHeight - 60 };
      const r = liveTarget.getBoundingClientRect();
      let tx = r.left + r.width / 2;
      let ty = r.top + r.height / 2;

      const isShopify = typeof window !== 'undefined' && !!window.Shopify;
      if (isShopify) {
        const cardCY = cardRect.top + cardRect.height / 2;
        if (ty < cardCY || (r.width === 0 && r.height === 0)) {
          tx = window.innerWidth - 60;
          ty = window.innerHeight - 60;
        }
      }
      return { x: tx, y: ty };
    };

    this.animatingId = product?.id ?? 'fly';

    const runAnimation = (visual) => {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = `
        position: fixed;
        z-index: 2147483647;
        left: ${cardRect.left}px;
        top: ${cardRect.top}px;
        width: ${cardRect.width}px;
        height: ${cardRect.height}px;
        pointer-events: none;
        perspective: 600px;
      `;

      visual.style.cssText = `
        width: 100%;
        height: 100%;
        border-radius: 12px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 8px 32px rgba(0,0,0,0.4);
        transform-origin: center top;
        transform: rotateX(0deg) scaleX(1) scaleY(1);
        object-fit: cover;
        transition: none;
      `;

      wrapper.appendChild(visual);
      document.body.appendChild(wrapper);
      cardElement.style.opacity = '0';
      cardElement.style.transition = 'opacity 0.08s ease';

      const startX = cardRect.left;
      const startY = cardRect.top;
      const startW = cardRect.width;
      const startH = cardRect.height;
      const cardCX = startX + startW / 2;
      const cardCY = startY + startH / 2;

      const startTime = performance.now();

      const animate = (now) => {
        const elapsed = now - startTime;
        const tRaw = Math.min(elapsed / DURATION, 1);
        const t = tRaw * tRaw * tRaw;
        const tSmooth = tRaw * tRaw;

        const { x: targetCX, y: targetCY } = getTargetCenter();
        const currentCX = cardCX + (targetCX - cardCX) * tSmooth;
        const currentCY = cardCY + (targetCY - cardCY) * t;

        const scale = Math.max(1 - tSmooth * 0.92, 0.08);
        const currentW = startW * scale;
        const currentH = startH * scale;

        wrapper.style.left = `${currentCX - currentW / 2}px`;
        wrapper.style.top = `${currentCY - currentH / 2}px`;
        wrapper.style.width = `${currentW}px`;
        wrapper.style.height = `${currentH}px`;

        const rotateX = tSmooth * 55;
        const scaleXVal = 1 - tSmooth * 0.5;
        const rotateY = tSmooth * 12;
        const rotateZ = Math.sin(tRaw * Math.PI * 3) * 8 * tSmooth;

        visual.style.transform = `
          rotateX(${rotateX}deg)
          rotateY(${rotateY}deg)
          rotateZ(${rotateZ}deg)
          scaleX(${scaleXVal})
        `;

        visual.style.opacity = `${tRaw < 0.7 ? 1 : 1 - (tRaw - 0.7) / 0.3}`;

        const shadowIntensity = (1 - tSmooth) * 0.4;
        wrapper.style.boxShadow = `
          0 ${12 * (1 - tSmooth)}px ${32 * (1 - tSmooth)}px rgba(0,0,0,${shadowIntensity}),
          0 0 ${20 * tSmooth}px rgba(139,92,246,${tSmooth * 0.4})
        `;

        if (tRaw < 0.9) {
          this._emitTrailAt(currentCX, currentCY + currentH * 0.3, tRaw);
        }

        if (tRaw < 1) {
          requestAnimationFrame(animate);
        } else {
          wrapper.remove();
          this.animatingId = null;

          this.onCartBump();
          this.onRipple();
          const finalTarget = getTargetCenter();
          this._emitImpactBurst(finalTarget.x, finalTarget.y);

          if (onComplete) onComplete();
        }
      };

      this._ensureCanvas();
      requestAnimationFrame(animate);
    };

    const preferClone = options.preferClone === true;

    if (preferClone) {
      const clone = cardElement.cloneNode(true);
      clone.querySelectorAll('button').forEach((b) => {
        b.style.pointerEvents = 'none';
        b.style.cursor = 'default';
      });
      runAnimation(clone);
    } else {
      html2canvas(cardElement, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        scale: 1,
        logging: false,
      })
        .then((captureCanvas) => {
          const img = document.createElement('img');
          img.src = captureCanvas.toDataURL('image/png');
          img.alt = '';
          runAnimation(img);
        })
        .catch(() => {
          const clone = cardElement.cloneNode(true);
          clone.querySelectorAll('button').forEach((b) => {
            b.style.pointerEvents = 'none';
            b.style.cursor = 'default';
          });
          runAnimation(clone);
        });
    }
  }

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
      this.canvas = null;
    }
    this._canvasInitialized = false;
    this.trailParticles = [];
    this.animatingId = null;
  }
}
